// Only these cross-origin documents may receive allow-same-origin. Never apply
// it to srcdoc, a blob created by the app, or any URL supplied by an artifact.
export const HTML_PREVIEW_ALLOW = 'fullscreen; autoplay; gamepad; clipboard-write; screen-wake-lock';
export const ISOLATED_HTML_SANDBOX = 'allow-scripts allow-same-origin allow-forms allow-modals allow-downloads allow-pointer-lock allow-popups allow-popups-to-escape-sandbox';
export const HTML_HOST_CHANNEL = 'gab-html-host-v1';
const origins = new Map();

export function previewToken() {
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
}

export function createPreviewHost(sourceKey, parentOrigin = window.location.origin, documentKey = '') {
  const parent = new URL(parentOrigin);
  const local = ['localhost', '127.0.0.1'].includes(parent.hostname) && parent.protocol === 'http:';
  const localPort = /^\d{1,5}$/.test(process.env.EXPO_PUBLIC_HTML_PREVIEW_PORT || '') ? process.env.EXPO_PUBLIC_HTML_PREVIEW_PORT : '8110';
  const domain = local ? `c.sites.localhost:${localPort}` : 'c.sites.gabusercontent.com';
  // Keep storage across Stop/Play, panel opening and restart in this app session.
  // IDs never derive from predictable file IDs, and aren't shared across tabs.
  const key = String(sourceKey || previewToken());
  if (!origins.has(key) || origins.get(key).document !== documentKey) {
    origins.delete(key);
    // Metadata can fall back to a filename. Different HTML with the same name
    // must still get a different origin. Bound retained source strings as well.
    let retained = [...origins.values()].reduce((sum, entry) => sum + entry.document.length, 0);
    while (origins.size && (origins.size >= 64 || retained + documentKey.length > 4_000_000)) {
      const oldest = origins.keys().next().value;
      retained -= origins.get(oldest).document.length; origins.delete(oldest);
    }
    origins.set(key, { token: previewToken(), document: documentKey });
  }
  const origin = `${local ? 'http' : 'https'}://preview--${origins.get(key).token}.${domain}`;
  const session = previewToken();
  return { origin, session, src: `${origin}/__gab/preview/v1#${encodeURIComponent(JSON.stringify({ session, parentOrigin: parent.origin }))}` };
}

export function connectPreviewHost({ host, getWindow, html, onAccepted, onError, timeoutMs = 15000 }) {
  let accepted = false;
  const timer = setTimeout(() => {
    cleanup();
    onError?.('Could not connect to the preview. Retry, or download the HTML to open it in your browser.');
  }, timeoutMs);
  const receive = event => {
    const data = event.data;
    if (event.source !== getWindow() || event.origin !== host.origin || data?.channel !== HTML_HOST_CHANNEL || data.session !== host.session) return;
    if (data.type === 'ready' && !accepted) {
      event.source.postMessage({ channel: HTML_HOST_CHANNEL, session: host.session, type: 'document', html }, host.origin);
    } else if (data.type === 'accepted' && !accepted) {
      accepted = true; cleanup(); onAccepted?.();
    }
  };
  function cleanup() { clearTimeout(timer); window.removeEventListener('message', receive); }
  window.addEventListener('message', receive);
  return cleanup;
}

export function openStandalonePreview(html, sourceKey, onError) {
  const host = createPreviewHost(`standalone:${sourceKey}`, window.location.origin, html);
  // Open synchronously in the user's click. The receiver drops opener before
  // running any generated script. Never write untrusted HTML into about:blank.
  let popup;
  const cleanup = connectPreviewHost({ host, getWindow: () => popup, html, onError });
  popup = window.open(host.src, '_blank');
  if (!popup) { cleanup(); onError('Your browser blocked the new tab. Allow pop-ups for Gab AI and try again.'); }
  return cleanup;
}
